import styled, { css } from "styled-components";
import { compassDarBlue, compassLightGreen, AI_GRADIENT } from "../../styles";

export const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #dbdade;
  color: black;
  width: 100%;
  height: 65px;
  font-size: 16px;
  font-weight: bold;
  padding: 0px;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #20e290;
  }
`;

export const CompassGreenButton = styled(Button)`
  background-color: ${compassLightGreen};
  width: auto;
  height: auto;
  padding: 10px 60px;
  font-family: "Apercu", sans-serif;
  font-size: 16px;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CompassWhiteButton = styled(Button)<{ size?: "s" | "m" }>`
  background-color: transparent;
  border: 1px solid ${compassDarBlue};
  width: auto;
  height: auto;
  padding: ${({ size }): string => (size === "s" ? "6px 16px" : "10px 60px")};
  margin-right: 10px;
  color: ${compassDarBlue};
  font-family: "Apercu", sans-serif;
  font-size: ${({ size }): string => (size === "s" ? "13px" : "16px")};
  white-space: nowrap;

  &:hover {
    color: white;
  }

  &:hover {
    background-color: ${compassDarBlue};
  }
`;

export const CompassGreenOutlineButton = styled(Button)<{
  size?: "s" | "m";
  $width?: string;
  $height?: string;
  $padding?: string;
}>`
  background-color: white;
  border: 1px solid ${compassLightGreen};
  color: ${compassLightGreen};
  width: ${({ $width }): string => $width || "100%"};
  height: ${({ $height }): string => $height || "65px"};
  padding: ${({ $padding }): string => $padding || "0px"};
  font-size: ${({ size }): string => (size === "s" ? "14px" : "16px")};

  &:hover {
    background-color: ${compassLightGreen};
    color: black;
  }

  &:disabled {
    border-color: #ccc;
    color: #ccc;
    cursor: not-allowed;

    &:hover {
      background-color: white;
      color: #ccc;
    }
  }
`;

export const UnStyledButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  z-index: 2;
`;

// =============================================================================
// AI BUTTON VARIANTS
// =============================================================================

export type AIButtonVariant = "outline" | "filled";

interface AIButtonProps {
  variant?: AIButtonVariant;
  size?: "s" | "m";
  $width?: string;
  $height?: string;
  $padding?: string;
}

const aiButtonOutlineStyles = css`
  position: relative;
  background: transparent;
  border: none;
  overflow: hidden;

  /* Gradient border using pseudo-element */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 30px;
    padding: 1.5px;
    background: ${AI_GRADIENT};
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    transition: opacity 0.4s ease;
  }

  /* Background fill overlay */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 30px;
    background: ${AI_GRADIENT};
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 0;
  }

  /* Gradient text */
  span {
    position: relative;
    z-index: 1;
    background: ${AI_GRADIENT};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    transition: all 0.4s ease;
  }

  &:hover {
    &::before {
      opacity: 0;
    }

    &::after {
      opacity: 1;
    }

    span {
      background: none;
      -webkit-background-clip: unset;
      background-clip: unset;
      -webkit-text-fill-color: white;
      color: white;
    }
  }
`;

const aiButtonFilledStyles = css`
  background: ${AI_GRADIENT};
  border: none;
  color: white;
  transition: filter 0.4s ease;

  span {
    color: white;
    -webkit-text-fill-color: white;
  }

  &:hover {
    filter: brightness(1.1) saturate(1.1);
  }
`;

export const CompassAIButton = styled(Button)<AIButtonProps>`
  width: ${({ $width }): string => $width || "auto"};
  height: ${({ $height }): string => $height || "auto"};
  padding: ${({ $padding }): string => $padding || "10px 40px"};
  font-family: "Apercu", sans-serif;
  font-size: ${({ size }): string => (size === "s" ? "14px" : "16px")};
  transition: all 0.2s ease;

  ${({ variant }): ReturnType<typeof css> =>
    variant === "filled" ? aiButtonFilledStyles : aiButtonOutlineStyles}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;

    /* Override hover styles when disabled */
    &:hover {
      background: ${({ variant }): string =>
        variant === "filled" ? AI_GRADIENT : "transparent"};
      filter: none;

      &::before {
        opacity: 1;
      }

      &::after {
        opacity: 0;
      }

      span {
        background: ${({ variant }): string =>
          variant === "filled" ? "none" : AI_GRADIENT};
        -webkit-background-clip: ${({ variant }): string =>
          variant === "filled" ? "unset" : "text"};
        background-clip: ${({ variant }): string =>
          variant === "filled" ? "unset" : "text"};
        -webkit-text-fill-color: ${({ variant }): string =>
          variant === "filled" ? "white" : "transparent"};
      }
    }
  }
`;
